import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type UIEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { cn } from '@/utils/cn';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PAGE_SCROLL_SELECTOR = 'main';
const DRAG_THRESHOLD = 0.15;

type Snap = 'half' | 'full';

export interface AssistantSheetProps {
  open: boolean;
  onClose: () => void;
  showScrim: boolean;
  initialSnap?: Snap;
  children: ReactNode;
}

/**
 * Mobile bottom sheet (<768px): half/full snap, drag + swipe-to-close, keyboard aware.
 * When closed, children stay mounted (hidden) so an in-flight stream survives.
 */
export function AssistantSheet({
  open,
  onClose,
  showScrim,
  initialSnap = 'half',
  children,
}: AssistantSheetProps) {
  const { t } = useTranslation();
  const inset = useVisualViewportInset();
  const [snap, setSnap] = useState<Snap>('half');
  const [dragDelta, setDragDelta] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<number | null>(null);
  const pullStart = useRef<number | null>(null);

  // Read through a ref so firstRun completing mid-open does not collapse the sheet.
  const initialSnapRef = useRef<Snap>(initialSnap);
  initialSnapRef.current = initialSnap;
  useEffect(() => {
    if (open) setSnap(initialSnapRef.current ?? 'half');
  }, [open]);

  // Page scroll lock (restored on close and unmount).
  useEffect(() => {
    if (!open) return;
    const page = document.querySelector<HTMLElement>(PAGE_SCROLL_SELECTOR);
    if (!page) return;
    const prevOverflow = page.style.overflow;
    const prevTop = page.scrollTop;
    page.style.overflow = 'hidden';
    page.scrollTop = prevTop;
    return () => {
      page.style.overflow = prevOverflow;
      page.scrollTop = prevTop;
    };
  }, [open]);

  // Dialog semantics: focus, Esc, Tab trap, focus restore.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      const root = sheetRef.current;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !root) return;
      const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [open, onClose]);

  const settle = useCallback(
    (delta: number) => {
      const limit = window.innerHeight * DRAG_THRESHOLD;
      if (delta < -limit && snap === 'half') setSnap('full');
      else if (delta > limit) {
        if (snap === 'full') setSnap('half');
        else onClose();
      }
    },
    [snap, onClose],
  );

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    dragStart.current = e.clientY;
    setDragDelta(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (dragStart.current === null) return;
    setDragDelta(e.clientY - dragStart.current);
  };
  const onPointerUp = (e: PointerEvent<HTMLElement>) => {
    if (dragStart.current === null) return;
    const delta = e.clientY - dragStart.current;
    dragStart.current = null;
    setDragDelta(null);
    settle(delta);
  };

  const onHandleKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSnap('full');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (snap === 'full') setSnap('half');
      else onClose();
    }
  };

  // Thread-driven snapping (README M1-M4).
  const onScrollCapture = (e: UIEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (snap === 'half' && target.closest?.('[role="log"]') && target.scrollTop > 0) {
      setSnap('full');
    }
  };
  const onTouchStartCapture = (e: React.TouchEvent<HTMLElement>) => {
    const log = (e.target as HTMLElement).closest?.('[role="log"]');
    pullStart.current = log && log.scrollTop === 0 ? e.touches[0].clientY : null;
  };
  const onTouchMoveCapture = (e: React.TouchEvent<HTMLElement>) => {
    if (pullStart.current === null) return;
    const delta = e.touches[0].clientY - pullStart.current;
    if (delta > window.innerHeight * DRAG_THRESHOLD) {
      pullStart.current = null;
      settle(delta);
    }
  };

  const dragging = dragDelta !== null && dragDelta !== 0;
  const height =
    snap === 'half' ? '56vh' : `calc(100dvh - 52px - ${inset}px)`;
  const translate = dragging && dragDelta > 0 ? dragDelta : 0;

  return createPortal(
    <>
      {open && showScrim && (
        <div
          data-testid="assistant-sheet-scrim"
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-[rgba(22,36,30,0.24)]"
        />
      )}
      <div
        ref={sheetRef}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
        hidden={!open}
        aria-label={t('assistant.panel.regionLabel')}
        tabIndex={-1}
        data-snap={snap}
        onScrollCapture={onScrollCapture}
        onTouchStartCapture={onTouchStartCapture}
        onTouchMoveCapture={onTouchMoveCapture}
        style={{
          bottom: inset,
          height,
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: translate ? `translateY(${translate}px)` : undefined,
        }}
        className={cn(
          'fixed inset-x-0 z-50 flex-col rounded-t-[16px] bg-[var(--background-page-content)] shadow-[0_-8px_30px_rgba(12,61,45,0.2)] outline-none',
          open ? 'flex' : 'hidden',
          !dragging &&
            'transition-[height] duration-[280ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none',
        )}
      >
        <TeeButton
          variant="ghost"
          aria-label={t(
            snap === 'half'
              ? 'assistant.panel.sheetExpand'
              : 'assistant.panel.sheetCollapse',
          )}
          data-testid="assistant-sheet-handle"
          className="h-6 w-full shrink-0 touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onHandleKey}
        >
          <span className="block h-1 w-9 rounded-[2px] bg-[var(--border-default)]" />
        </TeeButton>
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </>,
    document.body,
  );
}
